import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  Renderer2,
  HostListener,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UsersService } from 'src/app/services/users/users.service';
import { TranslateService } from '@ngx-translate/core';
import { UserInfo } from '../../../models/user-info.model';
import { Router } from '@angular/router';
import { Error as EntError } from 'src/app/models/error.model';
import { Workbook } from 'exceljs';
import { entAnimations } from 'src/app/animations';
import {
  Subscription,
  lastValueFrom,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
} from 'rxjs';
import { ImageService } from 'src/app/services/profile-picture/images.service';
import { FormControl } from '@angular/forms';
import { SuccesSnackbarComponent } from '../../snackbars/succes-snackbar/success-snackbar.component';
import { ErrorSnackbarComponent } from '../../snackbars/error-snackbar/error-snackbar.component';
import { LoadingSnackbarComponent } from '../../snackbars/loading-snackbar/loading-snackbar.component';
import { ProfilePictureExport } from 'src/app/models/profilePictureExport.model';
import { MatTableDataSource } from '@angular/material/table';
import { Sort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { MyUsers } from 'src/app/models/my-users.model';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { UserRightsHelper } from 'src/app/models/role.model';
import JSZip from 'jszip';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class UsersComponent implements OnInit, OnDestroy {
  //Pagination
  private loadedUsers = 0;
  allowedPageSize: number[] = [25, 50, 100];
  selectedPageSize: number = this.allowedPageSize[0];
  currentPage = 0; //0 based page index
  hasMoreUsers = false;
  private contextStorage: {
    pageIndex: number;
    pageSize: number;
    loadedUsers: number;
    lastNameFilter: string;
    firstNameFilter: string;
    statusFilter: string;
    classFilter: string;
    hasMoreUsers: boolean;
  };

  users: MyUsers[];
  isLoading = false;
  private currentUser: UserInfo = new UserInfo();

  private readonly snackbarDisplayTime: number = 3000;
  private readonly timeOutNumber: number = 300000;

  showFilters = true;
  showHideFilter = false;
  lastNameSearchField: FormControl = new FormControl('');
  firstNameSearchField: FormControl = new FormControl('');
  classSearchField: FormControl = new FormControl('');
  statusSearchField: FormControl = new FormControl('');

  private lastNameSearchSub: Subscription;
  private firstNameSearchSub: Subscription;
  private classSearchSub: Subscription;
  private statusSearchSub: Subscription;
  private currentGeSub: Subscription;

  private selectedEtablissement: string;
  errorGetUsers = false;
  error: EntError = { error: { message: '', code: '' } };

  imageToolTip: string | null = null;
  private readonly loadingImage: string = 'assets/images/avatars/avatar.png'; // Placeholder image
  isImageLoaded = false;

  private isExportISM = false;

  dataSource = new MatTableDataSource<MyUsers>([]);
  selection = new SelectionModel<MyUsers>(true, []);

  displayedColumns: string[] = [];
  expandedElement: MyUsers | null;
  windowWidth: number = window.innerWidth;
  sortedValue: string;

  private showPicture = false;

  constructor(
    private _userService: UsersService,
    private _translateService: TranslateService,
    private _imageService: ImageService,
    private _router: Router,
    private _renderer: Renderer2,
    private _element: ElementRef,
    private _snackBar: MatSnackBar,
    private _localStorageService: LocalStorageService,
    private _settingsService: SettingsService
  ) {}

  ngOnDestroy(): void {
    this.firstNameSearchSub.unsubscribe();
    this.lastNameSearchSub.unsubscribe();
    this.classSearchSub.unsubscribe();
    this.statusSearchSub.unsubscribe();
    this.currentGeSub.unsubscribe();
  }

  ngOnInit() {
    this.onResize();

    this.selectedEtablissement =
      this._localStorageService.getItem<string>('userDefaultGE');

    this.currentGeSub = this._settingsService.currentGE$.subscribe(ge => {
      this.selectedEtablissement = ge;
      this.onEtablissementChange();
    });

    this.currentUser = this._userService.currentUserValue;
    if (this.currentUser == null) {
      this._router.navigate(['/login']);
    }

    this.showPicture = this.showPictureColumn();
    this.updateDisplayedColumns();

    //Save and load the context manually.
    const context = this._localStorageService.getItem<
      typeof this.contextStorage
    >('contextStorageUsersManagement');

    if (context !== null) {
      this.contextStorage = {
        classFilter: context.classFilter,
        firstNameFilter: context.firstNameFilter,
        lastNameFilter: context.lastNameFilter,
        statusFilter: context.statusFilter,
        loadedUsers: context.loadedUsers,
        pageIndex: context.pageIndex,
        pageSize: context.pageSize,
        hasMoreUsers: context.hasMoreUsers,
      };
      this.selectedPageSize = this.contextStorage.pageSize;
      this.loadedUsers = this.contextStorage.loadedUsers;
      this.currentPage = this.contextStorage.pageIndex;
      this.hasMoreUsers = this.contextStorage.hasMoreUsers;
      this.classSearchField.setValue(this.contextStorage.classFilter, {
        emitEvent: false,
      });
      this.firstNameSearchField.setValue(this.contextStorage.firstNameFilter, {
        emitEvent: false,
      });
      this.lastNameSearchField.setValue(this.contextStorage.lastNameFilter, {
        emitEvent: false,
      });
      this.statusSearchField.setValue(this.contextStorage.statusFilter, {
        emitEvent: false,
      });
      this.getUsers();
    } else {
      this.onEtablissementChange();
    }

    this.firstNameSearchSub = this.firstNameSearchField.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((data: string) => {
        let fieldValue = data.toLowerCase();
        fieldValue = fieldValue.charAt(0).toUpperCase() + fieldValue.slice(1);
        this.firstNameSearchField.setValue(fieldValue, {
          emitEvent: false,
        });
        this.resetValues();
        this.getUsers();
      });

    this.lastNameSearchSub = this.lastNameSearchField.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((data: string) => {
        let fieldValue = data.toLowerCase();
        fieldValue = fieldValue.charAt(0).toUpperCase() + fieldValue.slice(1);
        this.lastNameSearchField.setValue(fieldValue, {
          emitEvent: false,
        });
        this.resetValues();
        this.getUsers();
      });

    this.classSearchSub = this.classSearchField.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((data: string) => {
        this.classSearchField.setValue(data.toUpperCase(), {
          emitEvent: false,
        });
        this.resetValues();
        this.getUsers();
      });

    this.statusSearchSub = this.statusSearchField.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((data: string) => {
        this.statusSearchField.setValue(data.toUpperCase(), {
          emitEvent: false,
        });
        this.resetValues();
        this.getUsers();
        this.setOption(data); // Set the value to be displayed in Dropdown
      });
  }

  @HostListener('window:resize')
  onResize(): void {
    if (window.innerWidth <= 650) {
      this.showHideFilter = true;
    } else {
      this.showHideFilter = false;
      this.showFilters = true;
    }
    this.updateDisplayedColumns();
    this.windowWidth = window.innerWidth;
  }

  private updateDisplayedColumns(): void {
    if (window.innerWidth >= 2000) {
      this.displayedColumns = [
        'select',
        'mustChangePwd',
        'groupEtablissement',
        'classeArray',
        this.showPicture ? 'hasProfilePicture' : null,
        'firstName',
        'lastName',
        'activationCode',
        'officialemail',
        'username',
      ].filter(Boolean);
    } else if (window.innerWidth < 2000 && window.innerWidth >= 1700) {
      this.displayedColumns = [
        'select',
        'mustChangePwd',
        'classeArray',
        this.showPicture ? 'hasProfilePicture' : null,
        'firstName',
        'lastName',
        'activationCode',
        'officialemail',
        'username',
        'expand',
      ].filter(Boolean);
    } else if (window.innerWidth < 1700 && window.innerWidth >= 1500) {
      this.displayedColumns = [
        'select',
        'mustChangePwd',
        'classeArray',
        this.showPicture ? 'hasProfilePicture' : null,
        'firstName',
        'lastName',
        'officialemail',
        'username',
        'expand',
      ].filter(Boolean);
    } else if (window.innerWidth < 1500 && window.innerWidth >= 1300) {
      this.displayedColumns = [
        'select',
        'mustChangePwd',
        this.showPicture ? 'hasProfilePicture' : null,
        'firstName',
        'lastName',
        'officialemail',
        'username',
        'expand',
      ].filter(Boolean);
    } else if (window.innerWidth < 1300 && window.innerWidth >= 850) {
      this.displayedColumns = [
        'select',
        'mustChangePwd',
        this.showPicture ? 'hasProfilePicture' : null,
        'firstName',
        'lastName',
        'username',
        'expand',
      ].filter(Boolean);
    } else if (window.innerWidth < 850 && window.innerWidth >= 600) {
      this.displayedColumns = [
        'select',
        'mustChangePwd',
        this.showPicture ? 'hasProfilePicture' : null,
        'firstName',
        'lastName',
        'expand',
      ].filter(Boolean);
    } else if (window.innerWidth < 600 && window.innerWidth >= 500) {
      this.displayedColumns = [
        'select',
        'mustChangePwd',
        'firstName',
        'lastName',
        'expand',
      ];
    } else {
      this.displayedColumns = [
        'select',
        'mustChangePwd',
        'firstName',
        'expand',
      ];
    }
  }

  /**
   * Returns the one-based numbering representation of this.currentPage.
   */
  public get currentPageForView(): number {
    return this.currentPage + 1;
  }

  /**
   * Returns the number of pages available based on loaded users.
   */
  public get maxPage(): number {
    if (this.users == null || this.loadedUsers === 0) return 0;
    return Math.ceil(this.loadedUsers / this.selectedPageSize);
  }

  private initializeFilter() {
    this.dataSource.data = this.users;
    this.selection.clear();
  }

  toggleFilters() {
    const headerUsersElement =
      this._element.nativeElement.querySelector('.header-users');
    if (!this.showFilters) {
      if (headerUsersElement) {
        this._renderer.setStyle(headerUsersElement, 'max-height', '200px');
      }
    } else {
      if (headerUsersElement) {
        this._renderer.setStyle(headerUsersElement, 'max-height', '150px');
      }
    }
    this.showFilters = !this.showFilters;
  }

  onClearFilterClick() {
    this.resetFields();
    this.resetValues();
    this.getUsers();
  }

  // React to status dropdown change
  onSelectionChange(): void {
    const value = this.statusSearchField.value;
    this.setOption(value);
  }

  // Get status searchField value
  private getStatusFilter(value: string): boolean | null {
    if (value.toLocaleLowerCase() === 'active') {
      return false;
    } else if (value.toLocaleLowerCase() === 'inactive') {
      return true;
    } else {
      return null; // Representing both true and false
    }
  }

  // Set selected value in status dropdown, primary used when reset or loading context from cache
  setOption(option: string): void {
    this.statusSearchField.setValue(option, { emitEvent: false });
  }

  private async showSnackbar(
    type: 'success' | 'error' | 'loading',
    milliseconds: number
  ) {
    this._snackBar.dismiss();

    if (type == 'success') {
      this._snackBar.openFromComponent(SuccesSnackbarComponent, {
        data: this._translateService.instant('TABLE.EXPORT.SUCCESS'),
        duration: milliseconds,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
    } else if (type == 'loading') {
      this._snackBar.openFromComponent(LoadingSnackbarComponent, {
        data: this._translateService.instant('TABLE.EXPORT.LOADING'),
        duration: milliseconds,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
    } else {
      this._snackBar.openFromComponent(ErrorSnackbarComponent, {
        data: this._translateService.instant('TABLE.EXPORT.ERROR'),
        duration: milliseconds,
        horizontalPosition: 'right',
        verticalPosition: 'top',
      });
    }
  }

  async exportDataWithTimeout(selected: boolean) {
    await this.runWithTimeout(selected, false)
      .then(() => {
        this.showSnackbar('success', this.snackbarDisplayTime);
      })
      .catch(() => {
        this.showSnackbar('error', this.snackbarDisplayTime);
      });
  }

  async exportDataWithTimeoutInfraPcProfi(selected: boolean) {
    await this.runWithTimeout(selected, true)
      .then(() => {
        this.showSnackbar('success', this.snackbarDisplayTime);
      })
      .catch(() => {
        this.showSnackbar('error', this.snackbarDisplayTime);
      });
  }

  private timeout(ms: number): Promise<never> {
    return new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Operation timed out'));
      }, ms);
    });
  }

  private async runWithTimeout(selected: boolean, isInfraPcProf: boolean) {
    if (isInfraPcProf) {
      return await Promise.race([
        this.exportData(selected, true),
        this.timeout(this.timeOutNumber),
      ]);
    }
    return await Promise.race([
      this.exportData(selected, false),
      this.timeout(this.timeOutNumber),
    ]);
  }

  private async exportData(selected: boolean, isPcProfi: boolean) {
    this.showSnackbar('loading', this.timeOutNumber);
    const MAX_INT32 = 2 ** 31 - 1;
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Etudiants');

    let users: MyUsers[] = [];

    if (selected) {
      users = this.selection.selected;
    } else {
      users = await lastValueFrom(
        this._userService.getAllmyUsers(
          this.selectedEtablissement,
          0,
          MAX_INT32,
          this.classSearchField.value ?? '',
          this.firstNameSearchField.value ?? '',
          this.lastNameSearchField.value ?? '',
          this.getStatusFilter(this.statusSearchField.value)
        )
      );
    }

    const { columns, getRowData } = this.getExportConfig(isPcProfi);

    worksheet.columns = columns;
    worksheet.autoFilter = `A1:${String.fromCharCode(64 + columns.length)}1`;

    users.forEach(user => {
      worksheet.addRow(getRowData(user));
    });

    worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
        if (rowNumber === 1) cell.font = { bold: true };
        if (colNumber === 1 || rowNumber === 1) {
          cell.alignment = { horizontal: 'center' };
        }
      });
    });

    worksheet.views = [
      { state: 'frozen', xSplit: 0, ySplit: 1, activeCell: 'B2' },
    ];

    const today = new Date();

    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();

    const formattedDate = `${dd}.${mm}.${yyyy}`;

    const fileName = isPcProfi ? `UsersPcProfi_${this.selectedEtablissement}_${formattedDate}.xlsx` : 'Users.xlsx';
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  private getExportConfig(isPcProfi: boolean) {
    if (isPcProfi) {
      return {
        columns: [
          {
            header: this._translateService.instant(
              'USERS.SINGLE.INFO.USERNAME_EXPORT'
            ),
            key: 'username',
            width: 30,
          },
          {
            header: this._translateService.instant('USERS.SINGLE.INFO.EMAIL_EXPORT'),
            key: 'email',
            width: 30,
          },
          {
            header: this._translateService.instant(
              'USERS.SINGLE.INFO.LASTNAME'
            ),
            key: 'lastName',
            width: 30,
          },
          {
            header: this._translateService.instant(
              'USERS.SINGLE.INFO.FIRSTNAME'
            ),
            key: 'firstName',
            width: 30,
          },
          {
            header: this._translateService.instant(
              'USERS.SINGLE.INFO.DATEOFBIRTH'
            ),
            key: 'dateOfBirth',
            width: 30,
          },
          {
            header: this._translateService.instant('USERS.SINGLE.INFO.CLASS'),
            key: 'classe',
            width: 30,
          },
          {
            header: this._translateService.instant('USERS.SINGLE.INFO.GROUPS'),
            key: 'groups',
            width: 30,
          },
          {
            header: this._translateService.instant(
              'USERS.SINGLE.INFO.PREFERREDLANGUAGE'
            ),
            key: 'preferredLanaguage',
            width: 30,
          },
          {
            header: this._translateService.instant(
              'USERS.SINGLE.INFO.PASSWORD'
            ),
            key: 'password',
            width: 30,
          },
          {
            header: this._translateService.instant('USERS.SINGLE.INFO.ISM'),
            key: 'ISM nbr',
            width: 30,
          },
          {
            header: this._translateService.instant('USERS.SINGLE.INFO.ESCADA'),
            key: 'Escada nbr',
            width: 30,
          },
        ],
        getRowData: (user: any) => [
          user.userName,
          user.officialEmail,
          user.firstName,
          user.lastName,
          user.dateOfBirth,
          user.classes.join(','),
          '', // groups
          user.preferredLanaguage,
          '', // password
          user.idISM ?? '',
          '',
        ],
      };
    }

    return {
      columns: [
        {
          header: this._translateService.instant('TABLE.USERS.HEADER.STATUS'),
          key: 'mustChangePwd',
          width: 30,
        },
        {
          header: this._translateService.instant('USERS.SINGLE.INFO.SCHOOL'),
          key: 'groupEtablissement',
          width: 30,
        },
        {
          header: this._translateService.instant('USERS.SINGLE.INFO.CLASS'),
          key: 'classe',
          width: 30,
        },
        {
          header: this._translateService.instant(
            'USERS.SINGLE.INFO.HASPROFILEPICTURE'
          ),
          key: 'hasProfilePicture',
          width: 30,
        },
        {
          header: this._translateService.instant('USERS.SINGLE.INFO.FIRSTNAME'),
          key: 'firstName',
          width: 30,
        },
        {
          header: this._translateService.instant('USERS.SINGLE.INFO.LASTNAME'),
          key: 'lastName',
          width: 30,
        },
        {
          header: this._translateService.instant('USERS.SINGLE.INFO.USERNAME'),
          key: 'username',
          width: 30,
        },
        {
          header: this._translateService.instant(
            'USERS.SINGLE.INFO.INITIALPASSWORD'
          ),
          key: 'activationCode',
          width: 30,
        },
        {
          header: this._translateService.instant('USERS.SINGLE.INFO.EMAIL'),
          key: 'email',
          width: 30,
        },
      ],
      getRowData: (user: any) => [
        user.mustChangePwd
          ? this._translateService.instant('GENERAL.WAITING')
          : this._translateService.instant('TABLE.USERS.HEADER.TRUETEXT'),
        user.groupEtablissement.join(','),
        user.classes.join(','),
        user.hasProfilePicture,
        user.firstName,
        user.lastName,
        user.userName,
        user.activationCode,
        user.officialEmail,
      ],
    };
  }

  async exportAllImages(all?: boolean) {
    this.showSnackbar('loading', this.timeOutNumber);
    let myUsers: MyUsers[];
    // Check if any filters applied
    if (all) {
      //Refactor: roundtrip
      //Fetchs all userOids to then send them through request 'exportProfilePicture'
      myUsers = await firstValueFrom(
        this._userService.getAllmyUsers(this.selectedEtablissement)
      );
    } else {
      myUsers = this._localStorageService.getItem<MyUsers[]>('myUsers') || [];
    }
    let imagesToExport: ProfilePictureExport[] = [];
    if (myUsers && Array.isArray(myUsers)) {
      for (const myUser of myUsers) {
        imagesToExport.push({
          userOid: myUser.oid,
          classe: myUser.classes.join(';'),
          idISM: myUser.idISM,
          firstname: myUser.firstName,
          lastname: myUser.lastName,
        });
      }
      const userOids = imagesToExport.map(user => user.userOid);
      const images = await lastValueFrom(
        this._imageService.exportProfilePicture(userOids)
      );
      const numberOfImages = images.length;

      imagesToExport = imagesToExport.filter(user => {
        for (let j = 0; j < numberOfImages; j++) {
          if (user.userOid === images[j].id) {
            user.profilePicture = images[j].imageData;
            return true;
          }
        }
        return false;
      });
    }
    this.zipAndDownload(imagesToExport);
  }

  isISM() {
    this.isExportISM = true;
  }

  private isEmpty(value: string | unknown) {
    return (
      value == null || (typeof value === 'string' && value.trim().length === 0)
    );
  }

  private zipAndDownload(images: ProfilePictureExport[]) {
    const zip = new JSZip();
    let folderName: string;

    if (images && Array.isArray(images)) {
      images.forEach(user => {
        const base64 = user.profilePicture;
        const cleanBase64 = base64.split(',')[1]; // Remove the data URI prefix if it exists
        const fileType = this.getFileType(base64);
        let fileName: string;
        if (!this.isExportISM) {
          fileName = `${user.classe + '_' + user.lastname + '_' + user.firstname}.${fileType}`;
          folderName = 'images-nom.zip';
        } else {
          if (this.isEmpty(user.idISM) || user.idISM === '0') {
            fileName = `${user.lastname + '_' + user.firstname}.${fileType}`; // Set lastname and firstname as default when Id 0 or empty, else, duplicate values won't be zipped
          } else {
            fileName = `${user.idISM}.${fileType}`;
          }
          folderName = 'images-id.zip';
        }
        zip.file(fileName.toLowerCase(), cleanBase64, { base64: true });
      });

      zip.generateAsync({ type: 'blob' }).then(content => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(content);
        link.download = folderName;
        link.click();
        URL.revokeObjectURL(link.href);
      });
      this.isExportISM = false; // Reset
      this.showSnackbar('success', this.snackbarDisplayTime);
    } else {
      return null;
    }
  }

  // Get file type for download
  private getFileType(base64: string): string {
    const prefix = base64.split(',')[0];
    if (prefix.indexOf('image/png') !== -1) {
      return 'png';
    } else if (prefix.indexOf('image/jpeg') !== -1) {
      return 'jpg';
    } else if (prefix.indexOf('image/gif') !== -1) {
      return 'gif';
    }
    return 'bin'; // Default or unknown type
  }

  private filterItem(item: string) {
    return {
      text: item,
      value: item,
    };
  }

  /**
   * @description
   * Navigate to the user page
   * @param e Row data
   */
  onRowClick(e: UserInfo) {
    if (e.oid === this.currentUser.oid) {
      this._router.navigate(['/self']);
    } else {
      this._router.navigate(['/user/' + e.oid]);
    }
  }

  /**
   * @description
   * Initialize current page and users list.
   * Forces an API call on the next pagnination event.
   */
  private resetValues() {
    this.currentPage = 0;
    this.loadedUsers = 0;
    this.users = [];
    this._localStorageService.removeItem('myUsers');
    this.dataSource.data = this.users;
    this.selection.clear();
  }

  /**
   * Reset the content of the 3 search fields.
   */
  private resetFields() {
    this.firstNameSearchField.reset('', { emitEvent: false });
    this.lastNameSearchField.reset('', { emitEvent: false });
    this.classSearchField.reset('', { emitEvent: false });
    this.statusSearchField.reset('', { emitEvent: false });
  }

  private onEtablissementChange() {
    this.resetValues();
    this.resetFields();
    this.getUsers();
  }

  onPageSizeClick(pageSize: number) {
    this.selectedPageSize = pageSize;
    this.resetValues();
    this.resetFields();
    this.getUsers();
  }

  onPreviousPageclick() {
    this.currentPage--;
    this.getUsers();
  }

  onNextPageClick() {
    this.currentPage++;
    this.getUsers();
  }

  onRefreshClick() {
    this.resetValues();
    this.getUsers();
  }

  private showPictureColumn(): boolean {
    const roleChecker = new UserRightsHelper(
      this.currentUser.roles,
      this.currentUser.orgs
    );
    return roleChecker.canViewPictures();
  }

  /**
   * Load users from cache or API
   */
  private getUsers() {
    this.errorGetUsers = false;
    this.error = { error: { message: '', code: '' } };

    //Hit the cache first
    const usersFromCache: MyUsers[] =
      this._localStorageService.getItem<MyUsers[]>('myUsers') || [];
    if (usersFromCache.length > 0) {
      const start = this.currentPage * this.selectedPageSize;
      const end = (this.currentPage + 1) * this.selectedPageSize;
      const paginatedResult = usersFromCache.slice(start, end);
      if (paginatedResult.length > 0) {
        this.users = paginatedResult;
        this.dataSource.data = this.users;
        this.selection.clear();
        this.setDataSource();
        return;
      }
    }

    this.isLoading = true;
    this._userService
      .getAllmyUsers(
        this.selectedEtablissement,
        this.currentPage * this.selectedPageSize,
        this.selectedPageSize + 1, //Get one more for 'hasMoreUsers'
        this.classSearchField.value,
        this.firstNameSearchField.value,
        this.lastNameSearchField.value,
        this.getStatusFilter(this.statusSearchField.value)
      )
      .subscribe({
        next: result => {
          this.hasMoreUsers = result.length > this.selectedPageSize;
          this.users = result.slice(0, this.selectedPageSize); //Removes the additional user
          this.dataSource.data = this.users;
          this.selection.clear();
          if (usersFromCache.length > 0) {
            this._localStorageService.setItem(
              'myUsers',
              usersFromCache.concat(this.users)
            );
            this.loadedUsers = usersFromCache.length + this.users.length;
          } else {
            this._localStorageService.setItem('myUsers', this.users);
            this.loadedUsers = this.users.length;
          }
          this.setDataSource();
        },
        error: error => {
          let customError: EntError = { error: undefined };

          if (error.statusText == 'Unknown Error') {
            customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
          } else {
            if (error.error) {
              customError = error.error;
            }
            if (customError.error !== undefined) {
              if (customError.error.code == 'ERR403') {
                this._router.navigate(['/errors/error-403']);
              } else {
                this._router.navigate(['/errors/error-404']);
              }
            }
          }
          if (customError.error === undefined) {
            customError = { error: { message: 'Unknown Error', code: 'ERR2' } };
          }
          this.isLoading = false;
          this.errorGetUsers = true;
          this.error = customError;
        },
        complete: () => (this.isLoading = false),
      });
  }

  async getImageForToolTip(oid: string) {
    this.imageToolTip = this.loadingImage; // Set to placeholder default avatar while loading
    this.isImageLoaded = false; // Reset the loading state
    const imageSrc = await lastValueFrom(
      this._imageService.getProfilePicture(oid)
    );
    if (imageSrc !== null) {
      this.imageToolTip = imageSrc;
      this.isImageLoaded = true;
    } else {
      this.imageToolTip = this.loadingImage;
    }
  }

  checkPermission(): boolean {
    return this._imageService.checkPermissionAddImage(this.currentUser);
  }

  checkPermissionITManager2G(): boolean {
    const roleChecker = new UserRightsHelper(
      this.currentUser.roles,
      this.currentUser.orgs
    );
    if (roleChecker.isITManagerS2G()) {
      return true;
    }
    return false;
  }

  isS2G(): boolean {
    return this._imageService.checkIsS2G(this.currentUser);
  }

  private setDataSource() {
    this.contextStorage = {
      classFilter: this.classSearchField.value,
      firstNameFilter: this.firstNameSearchField.value,
      lastNameFilter: this.lastNameSearchField.value,
      statusFilter: this.statusSearchField.value,
      loadedUsers: this.loadedUsers,
      pageIndex: this.currentPage,
      pageSize: this.selectedPageSize,
      hasMoreUsers: this.hasMoreUsers,
    };

    this._localStorageService.setItem(
      'contextStorageUsersManagement',
      this.contextStorage
    );
    this.initializeFilter();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  checkboxLabel(row?: MyUsers): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} ${row.firstName + row.lastName}`;
  }

  sortData(sort: Sort) {
    this.sortedValue = sort.active;
    if (!sort.active || sort.direction === '') {
      this.sortedValue = null;
      return;
    }

    this.dataSource.data = this.dataSource.data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'firstName':
          return this.compare(
            a.firstName.toLocaleLowerCase(),
            b.firstName.toLocaleLowerCase(),
            isAsc
          );
        case 'lastName':
          return this.compare(
            a.lastName.toLocaleLowerCase(),
            b.lastName.toLocaleLowerCase(),
            isAsc
          );
      }
    });
  }

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  toggleRow(element: MyUsers, event: MouseEvent): void {
    event.stopPropagation();

    this.expandedElement = this.expandedElement === element ? null : element;
  }
}
